/* global Word */
import type { TemplateProfile } from '../templates';
import type { ElementDefinition } from './types';
import { runAtCursor } from '../word/context/runAtCursor';

const DISCLAIMER_TEXT =
  'This presentation is for information purposes only and does not constitute an offer to sell, solicitation of an offer to buy, or recommendation of any mutual fund units/securities or investment strategy. The views expressed herein are based on internal data, publicly available information and other sources believed to be reliable; however, no representation or warranty, express or implied, is made as to the accuracy, completeness, or reliability of such information. Any calculations or projections are illustrative in nature and are based on certain assumptions and should not be relied upon as the sole basis for any decision. The views and information contained herein are general in nature, do not take into account the specific investment objectives, financial situation or needs of any particular person, and are not intended to provide, and should not be construed as, investment, legal, tax or other professional advice. These views alone are not sufficient and should not be used for the development or implementation of any investment strategy. Investors are advised to independently evaluate the risks associated with investments and seek appropriate professional advice before making any investment decisions. All opinions and estimates included herein are subject to change without notice and represent the views as of the date of this material. Neither SBI Funds Management Limited, SBI Mutual Fund, SBI Mutual Fund Trustee Company Private Limited nor any of its directors, employees or affiliates shall be liable for any direct, indirect, incidental or consequential loss or damage arising from the use of or reliance on this information. The recipient of this material acknowledges that any reliance placed on this information is strictly at their own risk and agrees to rely on their own investigation and independent judgment.';

function toHex(color: string): string {
  return color.startsWith('#') ? color : `#${color}`;
}

/** Element definition for disclaimer insertion. */
export const disclaimerElement: ElementDefinition = {
  id: 'disclaimer',
  label: 'Disclaimer',
  icon: 'ShieldPersonRegular',
  description: 'Insert disclaimer block with separators',
  category: 'document',
  async insertAsync(profile: TemplateProfile): Promise<void> {
    await runAtCursor(async (range, context) => {
      const lineTable = range.insertTable(1, 1, Word.InsertLocation.before, [['']]);

      const outside = lineTable.getBorder(Word.BorderLocation.outside);
      outside.type = Word.BorderType.none;
      const inside = lineTable.getBorder(Word.BorderLocation.inside);
      inside.type = Word.BorderType.none;

      const bottom = lineTable.getBorder(Word.BorderLocation.bottom);
      bottom.type = Word.BorderType.single;
      bottom.color = toHex(profile.colorPalette.sourceText);
      bottom.width = 1.5;
      lineTable.rows.getFirst().setHeight(0, Word.RowHeightRule.exactly);

      const textPara = range.insertParagraph(DISCLAIMER_TEXT, Word.InsertLocation.after);
      textPara.font.name = profile.typography.fontFamily;
      textPara.font.size = profile.typography.sourceFontSize + 1.5;
      textPara.font.italic = true;
      textPara.alignment = Word.Alignment.justified;
      textPara.spaceBefore = 2;
      textPara.spaceAfter = 12;

      const warningTable = textPara.insertTable(1, 1, Word.InsertLocation.after, [['']]);
      const warningOutside = warningTable.getBorder(Word.BorderLocation.outside);
      warningOutside.type = Word.BorderType.none;
      const warningInside = warningTable.getBorder(Word.BorderLocation.inside);
      warningInside.type = Word.BorderType.none;
      const warningTop = warningTable.getBorder(Word.BorderLocation.top);
      warningTop.type = Word.BorderType.single;
      warningTop.color = toHex(profile.colorPalette.sourceText);
      warningTop.width = 1.5;

      const warningCell = warningTable.rows.getFirst().cells.getFirst();
      const warningCellBody = warningCell.body;
      warningCellBody.clear();
      const warningPara = warningCellBody.paragraphs.getFirst();
      warningPara.insertText(
        'Mutual Fund investments are subjects to market risks, read all scheme related documents carefully.',
        Word.InsertLocation.start
      );
      warningPara.font.name = profile.typography.fontFamily;
      warningPara.font.size = profile.typography.graphHintSize;
      warningPara.font.bold = true;
      warningPara.font.italic = false;
      warningPara.alignment = Word.Alignment.centered;
      warningCell.verticalAlignment = Word.VerticalAlignment.top;

      await context.sync();
    });
  },
};
